import { getDB, getCollection as getRxCollection } from '@/rxdb';
import { getCurrentUserScope, getOfflineEntityId, getUserScope, setCurrentUserScope } from '@/rxdb/sessionScope';

const activeSyncs = new Set();
const registeredQueues = new Set();
const operationHandlers = new Map();
let serviceStarted = false;
let syncPaused = false;
let sessionTransition = Promise.resolve();
let online = true;
const statusListeners = new Set();

function errorMessage(error) {
  return error && error.response && error.response.data
    ? JSON.stringify(error.response.data)
    : (error && error.message) || 'Synchronization failed';
}

function pendingSelector() {
  return { syncStatus: { $in: ['pending', 'failed'] } };
}

function retryDelay(attempts) {
  return Math.min(300000, 5000 * (2 ** Math.max(0, attempts - 1)));
}

function isOnline() {
  return online && (typeof navigator === 'undefined' || navigator.onLine !== false);
}

function notifyStatus() {
  statusListeners.forEach(listener => listener(isOnline()));
}

const syncedListeners = new Set();

export function subscribeSynced(listener) {
  syncedListeners.add(listener);
  return () => syncedListeners.delete(listener);
}

function notifySynced(operation) {
  syncedListeners.forEach(listener => {
    try { listener(operation); } catch (e) { /* listener errors must not break sync */ }
  });
}

export function subscribeConnectivity(listener) {
  statusListeners.add(listener);
  listener(isOnline());
  return () => statusListeners.delete(listener);
}

function isSuccessfulResponse(response) {
  if (!response) return false;
  if (response.responseCode && response.responseCode !== '00') return false;
  if (response.success === false || response.succeeded === false) return false;
  if (response.status && Number(response.status) >= 400) return false;
  return response.responseCode === '00' || response.success === true || response.succeeded === true ||
    Object.prototype.hasOwnProperty.call(response, 'data');
}

export async function queueRecord(collectionName, record) {
  const db = await getDB();
  const collection = getRxCollection(db, collectionName);
  const now = new Date().toISOString();
  const existing = await collection.findOne(record.id).exec();
  const value = {
    ...(existing ? existing.toJSON() : {}),
    ...record,
    ownerId: getCurrentUserScope(),
    syncStatus: 'pending',
    syncError: null,
    syncAttempts: existing && existing.toJSON().syncAttempts || 0,
    lastSyncAttempt: existing && existing.toJSON().lastSyncAttempt || null,
    nextRetryAt: null,
    lastSyncedAt: existing && existing.toJSON().lastSyncedAt || null,
    clientUpdatedAt: now,
    updatedAt: now
  };
  await collection.upsert(value);
  return value;
}

export async function syncCollection(collectionName, dispatchRecord) {
  const ownerId = getCurrentUserScope();
  if (!ownerId || syncPaused || activeSyncs.has(collectionName) || !isOnline()) return;
  activeSyncs.add(collectionName);

  try {
    const db = await getDB();
    const collection = getRxCollection(db, collectionName);
    const docs = (await collection.find({ selector: pendingSelector() }).exec())
      .filter(doc => doc.toJSON().ownerId === ownerId);

    const now = Date.now();
    for (const doc of docs) {
      const current = doc.toJSON();
      if (current.nextRetryAt && new Date(current.nextRetryAt).getTime() > now) continue;
      if (activeSyncs.has(`${collectionName}:${current.id}`)) continue;
      activeSyncs.add(`${collectionName}:${current.id}`);

      try {
        const attempts = (current.syncAttempts || 0) + 1;
        await doc.atomicPatch({
          syncStatus: 'syncing',
          syncAttempts: attempts,
          lastSyncAttempt: new Date().toISOString(),
          nextRetryAt: null,
          syncError: null,
          updatedAt: new Date().toISOString()
        });
        const response = await dispatchRecord(current);
        if (!isSuccessfulResponse(response)) {
          throw new Error(response.responseMessage || 'Backend rejected the record');
        }
        const backendId = response && response.data && (
          response.data.id || response.data.questionId || response.data.SchoolQuestionId || response.data.schoolQuestionId
        );
        const backendData = response && response.data && typeof response.data === 'object'
          ? { data: { ...(current.data || {}), ...response.data } }
          : {};
        await doc.atomicPatch({
          ...(backendId ? { backendId } : {}),
          ...backendData,
          syncStatus: 'synced',
          syncError: null,
          nextRetryAt: null,
          lastSyncedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      } catch (error) {
        await doc.atomicPatch({
          syncStatus: 'failed',
          syncError: errorMessage(error),
          nextRetryAt: new Date(Date.now() + retryDelay((current.syncAttempts || 0) + 1)).toISOString(),
          updatedAt: new Date().toISOString()
        });
      } finally {
        activeSyncs.delete(`${collectionName}:${current.id}`);
      }
    }
  } finally {
    activeSyncs.delete(collectionName);
  }
}

export function registerSyncQueue(collectionName, dispatchRecord) {
  const key = `${collectionName}:${dispatchRecord}`;
  if (registeredQueues.has(key)) return;
  registeredQueues.add(key);

  const run = () => syncCollection(collectionName, dispatchRecord);
  if (typeof window !== 'undefined') window.addEventListener('online', run);
  run();
  setInterval(run, 15000);
}

export function registerSyncHandler(operationType, handler) {
  operationHandlers.set(operationType, handler);
}

export async function queueOperation({
  id,
  operationType = 'http',
  entityKey,
  method = 'POST',
  url,
  payload = {},
  headers = {},
  collectionName = null,
  localRecordId = null,
  clientUpdatedAt = null
}) {
  const db = await getDB();
  const now = new Date().toISOString();
  const operationId = id || `${operationType}:${entityKey}:${Date.now()}`;
  const existing = await db.syncOperations.findOne(operationId).exec();
  const previous = existing ? existing.toJSON() : {};
  const record = {
    ...previous,
    id: operationId,
    operationType,
    ownerId: getCurrentUserScope(),
    entityKey,
    method,
    url,
    payload,
    headers,
    syncStatus: 'pending',
    syncAttempts: previous.syncAttempts || 0,
    lastSyncAttempt: previous.lastSyncAttempt || null,
    lastSyncedAt: previous.lastSyncedAt || null,
    nextRetryAt: null,
    syncError: null,
    serverId: previous.serverId || null,
    collectionName,
    localRecordId,
    sequence: (previous.sequence || 0) + 1,
    clientUpdatedAt: clientUpdatedAt || now,
    createdAt: previous.createdAt || now
  };
  await db.syncOperations.upsert(record);
  syncOperations().catch(() => {});
  return record;
}

// A page refresh mid-upload leaves operations stuck in 'syncing', which the queue never picks up again.
async function recoverInterruptedOperations(db, minAgeMs) {
  const stuck = await db.syncOperations.find({ selector: { syncStatus: 'syncing' } }).exec();
  const now = Date.now();
  for (const doc of stuck) {
    const current = doc.toJSON();
    if (activeSyncs.has(`syncOperations:${current.id}`)) continue;
    const lastAttempt = current.lastSyncAttempt ? new Date(current.lastSyncAttempt).getTime() : 0;
    if (now - lastAttempt < minAgeMs) continue;
    await doc.atomicPatch({ syncStatus: 'pending', nextRetryAt: null });
  }
}

export async function syncOperations() {
  if (syncPaused || activeSyncs.has('syncOperations') || !isOnline()) return;
  const ownerId = getCurrentUserScope();
  if (!ownerId) return;
  activeSyncs.add('syncOperations');

  try {
    const db = await getDB();
    await recoverInterruptedOperations(db, 120000);
    const docs = (await db.syncOperations.find({ selector: pendingSelector() }).exec())
      .filter(doc => doc.toJSON().ownerId === ownerId);
    const now = Date.now();

    for (const doc of docs) {
      const current = doc.toJSON();
      if (current.nextRetryAt && new Date(current.nextRetryAt).getTime() > now) continue;
      const handler = operationHandlers.get(current.operationType);
      if (!handler || activeSyncs.has(`syncOperations:${current.id}`)) continue;
      activeSyncs.add(`syncOperations:${current.id}`);

      try {
        const attempts = current.syncAttempts + 1;
        await doc.atomicPatch({
          syncStatus: 'syncing',
          syncAttempts: attempts,
          lastSyncAttempt: new Date().toISOString(),
          syncError: null
        });
        const response = await handler(current);
        if (response && response.responseCode && response.responseCode !== '00') {
          throw new Error(response.responseMessage || 'Backend rejected the operation');
        }
        const responseData = response && response.data;
        await doc.atomicPatch({
          syncStatus: 'synced',
          lastSyncedAt: new Date().toISOString(),
          nextRetryAt: null,
          syncError: null,
          ...(responseData && responseData.id ? { serverId: responseData.id } : {})
        });
        if (current.collectionName && current.localRecordId) {
          const localCollection = getRxCollection(db, current.collectionName);
          const localDoc = await localCollection.findOne(current.localRecordId).exec();
          if (localDoc) await localDoc.atomicPatch({
            ...(responseData && typeof responseData === 'object' && !Array.isArray(responseData)
              ? { data: { ...localDoc.toJSON().data, ...responseData } } : {}),
            syncStatus: 'synced',
            syncError: null,
            lastSyncedAt: new Date().toISOString(),
            nextRetryAt: null,
            ...(responseData && responseData.id ? { serverId: responseData.id } : {})
          });
        }
        notifySynced(current);
      } catch (error) {
        const attempts = current.syncAttempts + 1;
        await doc.atomicPatch({
          syncStatus: 'failed',
          nextRetryAt: new Date(Date.now() + retryDelay(attempts)).toISOString(),
          syncError: errorMessage(error)
        });
      } finally {
        activeSyncs.delete(`syncOperations:${current.id}`);
      }
    }
  } finally {
    activeSyncs.delete('syncOperations');
  }
}

async function transitionUserSession(user, previousUser) {
  const nextOwner = typeof user === 'string' && user.startsWith('user:')
    ? user
    : getUserScope(user);
  if (!nextOwner) throw new Error('The authenticated account has no stable user identifier.');

  syncPaused = true;
  let prepared = false;
  try {
    while (activeSyncs.size > 0) {
      await new Promise(resolve => setTimeout(resolve, 25));
    }

    const db = await getDB();
    const oldOwner = getCurrentUserScope() || getUserScope(
      previousUser,
      typeof localStorage !== 'undefined' ? localStorage.getItem('###') : null
    ) || 'user:legacy:unassigned';

    const operations = await db.syncOperations.find().exec();
    for (const operationDoc of operations) {
      const operation = operationDoc.toJSON();
      if (operation.ownerId) continue;
      await operationDoc.atomicPatch({ ownerId: oldOwner });
    }

    const records = await db.offlineEntities.find().exec();
    for (const recordDoc of records) {
      const record = recordDoc.toJSON();
      const recordOwner = record.ownerId || oldOwner;
      const clientId = String(record.id).startsWith(`${record.entityType}:`)
        ? String(record.id).slice(String(record.entityType).length + 1)
        : String(record.id);
      const scopedId = getOfflineEntityId(record.entityType, clientId, recordOwner);

      if (record.id !== scopedId) {
        await db.offlineEntities.upsert({ ...record, id: scopedId, ownerId: recordOwner });
        const relatedOperations = await db.syncOperations
          .find({ selector: { localRecordId: record.id } }).exec();
        for (const operationDoc of relatedOperations) {
          const operation = operationDoc.toJSON();
          const oldPrefix = `${record.entityType}:${clientId}`;
          await operationDoc.atomicPatch({
            localRecordId: scopedId,
            ownerId: operation.ownerId || recordOwner,
            ...(operation.entityKey === oldPrefix
              ? { entityKey: scopedId }
              : {})
          });
        }
        await recordDoc.remove();
      } else if (!record.ownerId) {
        await recordDoc.atomicPatch({ ownerId: recordOwner });
      }
    }

    for (const collectionName of ['schoolquestions', 'cbtquestions', 'cbtsubmissions']) {
      const collection = getRxCollection(db, collectionName);
      const cachedRecords = await collection.find().exec();
      for (const recordDoc of cachedRecords) {
        if (!recordDoc.toJSON().ownerId) {
          await recordDoc.atomicPatch({ ownerId: oldOwner });
        }
      }
    }

    if (oldOwner !== nextOwner) {
      for (const collectionName of ['offlineEntities', 'schoolquestions', 'cbtquestions', 'cbtsubmissions']) {
        const collection = getRxCollection(db, collectionName);
        const cachedRecords = await collection.find().exec();
        for (const recordDoc of cachedRecords) {
          const record = recordDoc.toJSON();
          if (record.ownerId === oldOwner && record.syncStatus === 'synced') {
            await recordDoc.remove();
          }
        }
      }

      for (const operationDoc of operations) {
        const operation = operationDoc.toJSON();
        if (operation.ownerId === oldOwner && operation.syncStatus === 'synced') {
          await operationDoc.remove();
        }
      }
    }

    if (typeof localStorage !== 'undefined') {
      if (oldOwner !== nextOwner) {
        [
          '#####', 'StudId', 'StudentId', 'HomeworkId', 'ExamCode', 'ExamId',
          'TimeTableId', 'SchcbtheaderId', 'QuestionId', 'SchQuestionId'
        ].forEach(key => localStorage.removeItem(key));
      }
      setCurrentUserScope(nextOwner);
    }
    prepared = true;
  } finally {
    if (!prepared) syncPaused = false;
  }
  return nextOwner;
}

export function prepareUserSession(user, previousUser) {
  const transition = sessionTransition.then(
    () => transitionUserSession(user, previousUser),
    () => transitionUserSession(user, previousUser)
  );
  sessionTransition = transition.catch(() => {});
  return transition;
}

export function resumeUserSessionSync() {
  syncPaused = false;
  syncOperations().catch(() => {});
}

export async function offlineRequest(request) {
  await queueOperation(request);
  return {
    responseCode: '00',
    data: request.payload,
    offline: true
  };
}

export async function pullCollection({ fetchRecords, collectionName, mapRecord }) {
  if (!isOnline()) return [];
  const ownerId = getCurrentUserScope();
  if (!ownerId) return [];
  const records = await fetchRecords();
  const db = await getDB();
  const collection = db[collectionName];
  if (!collection) throw new Error(`RxDB collection not found: ${collectionName}`);
  const mappedRecords = [];
  for (const record of records) {
    const mapped = { ...mapRecord(record), ownerId };
    const existing = await collection.findOne(mapped.id).exec();
    const local = existing && existing.toJSON();
    if (local && ['pending', 'syncing', 'failed'].includes(local.syncStatus)) continue;
    if (local && local.clientUpdatedAt && mapped.serverUpdatedAt &&
      new Date(local.clientUpdatedAt).getTime() > new Date(mapped.serverUpdatedAt).getTime()) continue;
    mappedRecords.push(mapped);
  }
  if (mappedRecords.length) await collection.bulkUpsert(mappedRecords);
  return mappedRecords;
}

export function startSyncService() {
  if (serviceStarted) return;
  serviceStarted = true;
  const run = () => syncOperations().catch(() => {});
  if (typeof window !== 'undefined') {
    online = navigator.onLine !== false;
    window.addEventListener('online', () => { online = true; notifyStatus(); run(); });
    window.addEventListener('offline', () => { online = false; notifyStatus(); });
    window.addEventListener('focus', run);
    window.addEventListener('visibilitychange', run);
    notifyStatus();
  }
  const currentOwner = getCurrentUserScope();
  const adoptLegacyRecords = currentOwner
    ? prepareUserSession(currentOwner).then(() => resumeUserSessionSync())
    : Promise.resolve();
  adoptLegacyRecords
    .then(() => getDB())
    .then(db => recoverInterruptedOperations(db, 0))
    .then(run)
    .catch(() => {});
  setInterval(run, 15000);
}
